import { fetchAuthSession } from 'aws-amplify/auth';
import type { ApiError } from './types';
import { getEffectiveCompanyId } from '@/store/company-context-store';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Authenticated fetch wrapper that injects the Cognito ID token and X-Company-ID header.
 * Note: HTTP API Gateway JWT authorizer requires ID token (has `aud` claim),
 * not access token (has `client_id` claim instead).
 */
async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  let token: string | undefined;

  try {
    const session = await fetchAuthSession();
    token = session.tokens?.idToken?.toString();
  } catch {
    // Auth not available (dev mode) — proceed without token
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add company context header for multi-tenant data isolation
  // Skip for /api/users/me which is used to bootstrap the company context
  const companyId = getEffectiveCompanyId();
  if (companyId && !path.endsWith('/api/users/me')) {
    headers['X-Company-ID'] = companyId;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorBody: ApiError & { error?: string };
    try {
      errorBody = await response.json() as ApiError & { error?: string };
    } catch {
      errorBody = { error: 'Request Failed', message: `HTTP ${response.status}` };
    }
    // Support both { message } and { error } response formats
    const errorMessage = errorBody.message || errorBody.error || `HTTP ${response.status}`;
    const error = new Error(errorMessage) as Error & { status: number };
    error.status = response.status;
    throw error;
  }

  return response;
}

export const apiClient = {
  async get<T>(path: string): Promise<T> {
    const response = await authFetch(path);
    return response.json() as Promise<T>;
  },

  async post<T>(path: string, body: unknown): Promise<T> {
    const response = await authFetch(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<T>;
  },

  async put<T>(path: string, body: unknown): Promise<T> {
    const response = await authFetch(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<T>;
  },

  async patch<T>(path: string, body: unknown): Promise<T> {
    const response = await authFetch(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return response.json() as Promise<T>;
  },

  async delete<T = void>(path: string): Promise<T> {
    const response = await authFetch(path, { method: 'DELETE' });
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  },
};
